import { useState } from "react";
import type { TaskPriority, TaskType } from "@sales-ops-amplify/shared";
import { useCreateTask } from "@/hooks/useTasks";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
};

const TYPES: { id: TaskType; label: string }[] = [
  { id: "call", label: "Call" },
  { id: "manual_email", label: "Manual email" },
  { id: "linkedin_step_connect", label: "LinkedIn connect" },
  { id: "linkedin_step_message", label: "LinkedIn message" },
  { id: "linkedin_step_view_profile", label: "LinkedIn view profile" },
  { id: "action_item", label: "Action item" },
];

export function CreateTaskModal({ open, onClose, onCreated }: Props) {
  const createMut = useCreateTask();
  const [type, setType] = useState<TaskType>("action_item");
  const [title, setTitle] = useState("");
  const [dueAt, setDueAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [priority, setPriority] = useState<TaskPriority>("medium");
  const [leadName, setContactName] = useState("");
  const [company, setCompany] = useState("");
  const [dealName, setDealName] = useState("");
  const [description, setDescription] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function submit() {
    setError(null);
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    try {
      await createMut.mutateAsync({
        type,
        title: title.trim(),
        dueAt: dueAt ? new Date(dueAt).toISOString() : undefined,
        priority,
        leadName: leadName.trim() || undefined,
        company: company.trim() || leadName.trim() || "Task",
        dealName: dealName.trim() || title.trim(),
        description: description.trim() || undefined,
        linkedinUrl: linkedinUrl.trim() || undefined,
      });
      setTitle("");
      setDescription("");
      setContactName("");
      setCompany("");
      setDealName("");
      setLinkedinUrl("");
      onCreated?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
      <div className="w-full max-w-lg space-y-3 rounded-lg border border-border bg-surface p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Create task</h2>
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <label className="block text-xs">
          Type
          <select
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={type}
            onChange={(e) => setType(e.target.value as TaskType)}
          >
            {TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-xs">
          Title
          <input
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Call VP Sales"
          />
        </label>

        <div className="grid gap-2 sm:grid-cols-2">
          <label className="block text-xs">
            Due date
            <input
              type="date"
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />
          </label>
          <label className="block text-xs">
            Priority
            <select
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TaskPriority)}
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </label>
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          <label className="block text-xs">
            Contact
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={leadName}
              onChange={(e) => setContactName(e.target.value)}
            />
          </label>
          <label className="block text-xs">
            Company
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </label>
        </div>

        <label className="block text-xs">
          Deal / sequence name
          <input
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={dealName}
            onChange={(e) => setDealName(e.target.value)}
          />
        </label>

        {(type.startsWith("linkedin") || linkedinUrl) && (
          <label className="block text-xs">
            LinkedIn URL
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              placeholder="https://linkedin.com/in/…"
            />
          </label>
        )}

        <label className="block text-xs">
          Notes
          <textarea
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            className="rounded-md border border-border px-3 py-1.5 text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-md bg-brand px-3 py-1.5 text-sm text-white disabled:opacity-40"
            disabled={createMut.isPending}
            onClick={() => void submit()}
          >
            {createMut.isPending ? "Creating…" : "Create task"}
          </button>
        </div>
      </div>
    </div>
  );
}
